import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Loader2, RefreshCcw, ExternalLink } from 'lucide-react';
import { Button } from '../../ui/button';
import ZoneOverlay from './ZoneOverlay';

/**
 * ModuleCanvas
 * -------------
 * Renders a same-origin iframe of the target module and layers an overlay
 * that auto-detects interactive zones (elements with data-testid).
 *
 * Props:
 *   module: { id, name, route, testidPrefixes }
 *   mode: 'overview' | 'drilldown'
 *   activeStep: { testid, fallbackTestid, title, hint, ... } | null
 *   onZoneClick: (zone) => void
 *   onZonesDetected: (zones[]) => void
 */
const ModuleCanvas = ({
  module,
  mode = 'overview',
  activeStep = null,
  onZoneClick,
  onZonesDetected,
  disabledGroups,
}) => {
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [zones, setZones] = useState([]);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const rescanZones = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument;
      const win = iframe.contentWindow;
      if (!doc || !win) return;
      const prefixes = module?.testidPrefixes || [];
      const nodes = Array.from(doc.querySelectorAll('[data-testid]'));
      const filtered = prefixes.length
        ? nodes.filter((n) => {
            const t = n.getAttribute('data-testid') || '';
            return prefixes.some((p) => t.startsWith(p));
          })
        : nodes;

      const rect = iframe.getBoundingClientRect();
      const vw = win.innerWidth || rect.width;
      const vh = win.innerHeight || rect.height;

      const detected = filtered
        .map((n) => {
          // getBoundingClientRect inside the iframe returns coordinates
          // relative to the iframe's own viewport — which is exactly the
          // coordinate space our overlay (positioned over the iframe) lives in.
          const r = n.getBoundingClientRect();
          if (r.width < 4 || r.height < 4) return null;
          // Skip elements fully outside the iframe viewport.
          if (r.bottom < 0 || r.top > vh || r.right < 0 || r.left > vw) return null;
          // Skip non-visible elements.
          const style = win.getComputedStyle(n);
          if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return null;
          return {
            testid: n.getAttribute('data-testid'),
            tag: n.tagName.toLowerCase(),
            text: (n.textContent || '').trim().slice(0, 80),
            x: r.left,
            y: r.top,
            w: r.width,
            h: r.height,
          };
        })
        .filter(Boolean);
      setZones(detected);
      onZonesDetected?.(detected);
    } catch (e) {
      setError('Unable to read iframe content. ' + (e?.message || ''));
    }
  }, [module, onZonesDetected]);

  // Load handler
  const handleLoad = () => {
    setLoading(false);
    setError(null);
    // Delay to let React hydrate inside the iframe.
    setTimeout(rescanZones, 600);
    setTimeout(rescanZones, 1500);
  };

  // Rescan on iframe scroll/resize
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const win = iframe.contentWindow;
    if (!win) return;
    const handler = () => rescanZones();
    try {
      win.addEventListener('scroll', handler, true);
      win.addEventListener('resize', handler);
    } catch { /* cross-origin */ }
    return () => {
      try {
        win.removeEventListener('scroll', handler, true);
        win.removeEventListener('resize', handler);
      } catch { /* noop */ }
    };
  }, [rescanZones, reloadKey]);

  // Rescan when outer container resizes.
  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(() => rescanZones());
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [rescanZones]);

  // Poll lightly for dynamic content (timeline auto-scroll, lazy lists).
  useEffect(() => {
    const t = setInterval(rescanZones, 4000);
    return () => clearInterval(t);
  }, [rescanZones]);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full rounded-lg overflow-hidden border border-border bg-background/60"
      data-testid="visual-lab-canvas"
    >
      {/* Canvas toolbar */}
      <div className="absolute top-2 right-2 z-20 flex items-center gap-1.5 bg-background/90 backdrop-blur border border-border rounded-md px-1.5 py-1 shadow-sm">
        <span className="text-[10px] font-medium text-muted-foreground px-1">
          {zones.length} zones
        </span>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0"
          onClick={() => {
            setLoading(true);
            setReloadKey((k) => k + 1);
          }}
          data-testid="visual-lab-reload"
          title="Reload module"
        >
          <RefreshCcw className="w-3 h-3" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0"
          onClick={() => window.open(module.route, '_blank', 'noopener,noreferrer')}
          data-testid="visual-lab-open-new"
          title="Open module in new tab"
        >
          <ExternalLink className="w-3 h-3" />
        </Button>
      </div>

      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60 backdrop-blur-sm">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading {module.name}…
          </div>
        </div>
      )}

      {error && (
        <div className="absolute bottom-2 left-2 z-20 max-w-md bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 text-xs px-2 py-1 rounded">
          {error}
        </div>
      )}

      <iframe
        key={reloadKey}
        ref={iframeRef}
        src={module.route}
        title={module.name}
        onLoad={handleLoad}
        className="w-full h-full border-0"
        data-testid="visual-lab-iframe"
      />

      {/* Overlay rendered in the same positioning context as the iframe so
          bounding-client-rect values line up 1:1 (both measured from the
          iframe's own viewport, which is visually identical to this container). */}
      <ZoneOverlay
        zones={zones}
        mode={mode}
        activeStep={activeStep}
        onZoneClick={onZoneClick}
        disabledGroups={disabledGroups}
      />
    </div>
  );
};

export default ModuleCanvas;
